import { useMemo } from 'react'
import type { MetadataFields, Metadatafieldtypes } from '../../MetadataFields.js'
import { isExistingMetadataField } from '../../MetadataFields.js'
import type { BoundOperation } from '../../OperationTypes.js'
import { isFilterOperation } from '../../OperationTypes.js'
import type { FilterOperationKey } from '../../OperationRegistry.js'
import { OPERATION_REGISTRY, OPERATIONS } from '../../OperationRegistry.js'
import { FilterConfigEditor } from './FilterConfigEditor.js'
import { MetadataFieldSelect } from './MetadataFieldSelect.js'
import type { VisualizationSampleContainer } from '../../VisualizationSampleContainer.js'

type FilterEditorRowProps = {
  boundFilterOperation: BoundOperation<FilterOperationKey, unknown>
  onChange: (next: BoundOperation<FilterOperationKey, unknown>) => void
  onRemove: () => void
  samples: VisualizationSampleContainer[]
  metadataFields: MetadataFields
}

export function FilterEditorRow({
  boundFilterOperation: boundOperation,
  onChange,
  onRemove,
  samples,
  metadataFields
}: FilterEditorRowProps) {
  // Determine field descriptor
  const fieldDescriptor = metadataFields[boundOperation.metaDataField]

  const applicableOps = useMemo(() => {
    if (!fieldDescriptor) return []
    return OPERATIONS.filter((op) => op.type === 'filter' && op.applicableTo.includes(fieldDescriptor.type))
  }, [fieldDescriptor])

  const currentOp = OPERATION_REGISTRY[boundOperation.operationKey]

  const handleFieldChange = (newField: string) => {
    if (!isExistingMetadataField(newField, metadataFields)) return
    const newDescriptor = metadataFields[newField]
    const firstValid = OPERATIONS.filter((op) => isFilterOperation(op)).find((op) => op.applicableTo.includes(newDescriptor.type))
    if (!firstValid) return

    onChange({
      operationKey: firstValid.key,
      /* actually hand-wavy since it may be a custom type */
      metaDataField: newField as Metadatafieldtypes,
      config: firstValid.config?.defaultConfig ?? {}
    })
  }

  const handleOperatorChange = (opKey: FilterOperationKey) => {
    const newOp = OPERATION_REGISTRY[opKey]
    if (!newOp || !isFilterOperation(newOp)) return

    let newConfig: unknown = newOp.config?.defaultConfig ?? {}
    if (newOp.config?.verifyConfig && newOp.config.verifyConfig(boundOperation.config)) {
      newConfig = boundOperation.config
    }

    onChange({
      ...boundOperation,
      operationKey: newOp.key,
      config: newConfig
    })
  }

  const handleConfigChange = (nextConfig: unknown) => {
    onChange({ ...boundOperation, config: nextConfig })
  }

  return (
    <div className="flex flex-wrap gap-2 w-full items-center">
      {/* Field select */}
      <MetadataFieldSelect
        metadataFields={metadataFields}
        samples={samples}
        value={boundOperation.metaDataField}
        onChange={handleFieldChange}
      />

      {/* Operator select */}
      <select
        className="select select-sm select-bordered"
        value={boundOperation.operationKey}
        onChange={(e) => handleOperatorChange(e.target.value as FilterOperationKey)}
        disabled={!fieldDescriptor}
      >
        {applicableOps.map((op) => (
          <option key={op.key} value={op.key}>
            {op.label}
          </option>
        ))}
      </select>

      {/* Config input */}
      <FilterConfigEditor
        operation={isFilterOperation(currentOp) ? currentOp : undefined}
        config={boundOperation.config}
        onChange={handleConfigChange}
        field={fieldDescriptor}
        samples={samples}
      />

      {/* Remove button */}
      <button className="btn btn-xs btn-outline btn-error" onClick={onRemove}>
        ✕
      </button>
    </div>
  )
}
